import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import Loading from "../../components/loading";
import {Form, Formik} from "formik";
import {representativeRequestSchema} from "../../utils/schemas";
import FormGroup from "../../components/form-group";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useGlobalNotificationMessage} from "../../context/message";
import {useRepresentativeService} from "../../services/representative-service";
import AddressCell from "../../components/address-cell";
import ContactCell from "../../components/contact-cell";
import AddEditContactModal from "./add-edit-contact-modal";
import AddEditAddressModal from "./add-edit-address-modal";
import {useAddressService} from "../../services/address-service";
import {useContactService} from "../../services/contact-service";

const AddEditRepresentativeModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState();
  const [loading, setLoading] = useState(false);
  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [reset, setReset] = useState(0);

  const addEditContactModal = useRef();
  const addEditAddressModal = useRef();

  let formSchema = representativeRequestSchema;

  let representativeService = useRepresentativeService();
  let addressService = useAddressService();
  let contactService = useContactService();

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (model=null) => {
    setLoading(true);

    let promises = [
      Promise.resolve('initOk')
    ];

    if (model && model.id){
      promises.push(getDetail(model.id));
    }

    Promise.all(promises).then(([initOk, detail= {}])=>{
      let newDetail = Object.assign({}, detail);
      setModel(formSchema.cast(newDetail));
      setLoading(false);
    });

    setShow(true);
  }
  const closeModal = () => {
    setShow(false);
    setReset(reset+1);
    setModel(formSchema.cast());
  }

  const getDetail = (id) => {
    return representativeService.getDetail(id);
  }

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    removeMessage();
    let reqValues = {...values};

    representativeService.addEditModel(reqValues, reqValues.id)
      .then((res) => {
        if (onSuccess && 'function' === typeof onSuccess){
          onSuccess(res);
        }
        closeModal();
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa uložiť zmeny.");
        HelperService.handleFormErrors(error, setErrors);
      })
      .finally(()=>{ setSubmitting(false);});
  };

  const onAddEditContactCallback = (item)=>{
    let newModel = {...model};
    newModel.contacts = [...HelperService.addOrReplaceItemInList(newModel.contacts, item)];
    setModel(newModel);
  };

  const onAddEditAddressCallback = (item)=>{
    let newModel = {...model};
    newModel.addresses = [...HelperService.addOrReplaceItemInList(newModel.addresses, item)];
    setModel(newModel);
  };

  const delAddress = (item) => {
    if (true===confirm('Odstrániť adresu?')){
      addressService.delModel(item.id)
        .then(()=>{
          let newModel = {...model};
          newModel.addresses = [...HelperService.removeItemFromList(newModel.addresses, item)];
          setModel(newModel);
        })
        .catch(error => {
          HelperService.handleApiErrors(error, addErrorMessage);
        })
    }
  };

  const delContact = (item) => {
    if (true===confirm('Odstrániť kontakt?')){
      contactService.delModel(item.id)
        .then(()=>{
          let newModel = {...model};
          newModel.contacts = [...HelperService.removeItemFromList(newModel.contacts, item)];
          setModel(newModel);
        })
        .catch(error => {
          HelperService.handleApiErrors(error, addErrorMessage);
        })
    }
  };

  return (
    <>
      <Formik
        key={reset}
        enableReinitialize={true}
        initialValues={formSchema.cast(model) || formSchema.cast()}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, handleSubmit, setFieldValue}) => (

          <Modal size="lg" show={!!show} onHide={closeModal} centered>
            <Modal.Header closeButton>
              { model && <Modal.Title>{ !model.id ? 'Nový delegát' : 'Úprava delegáta' }</Modal.Title>}
            </Modal.Header>
            <Modal.Body>

              <AddEditContactModal ref={addEditContactModal} onSuccess={(item)=>onAddEditContactCallback(item)} />
              <AddEditAddressModal ref={addEditAddressModal} onSuccess={(item)=>onAddEditAddressCallback(item)} />

              <Section animation={HelperService.getRandomAnimation()}>

                {loading && <Loading className="m-2" />}

                {!loading && <Form className="row">

                  <FormGroup
                    name="title_before"
                    placeholder="titul"
                    label="Titul pred menom"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="first_name"
                    placeholder="meno"
                    label="Meno"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="last_name"
                    placeholder="priezvisko"
                    label="Priezvisko"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="title_after"
                    placeholder="titul"
                    label="Titul za menom"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="birth_number"
                    placeholder="Rodné číslo"
                    label="Rodné číslo"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="birth_at"
                    type="date"
                    placeholder="Dátum narodenia"
                    label="Dátum narodenia"
                    className="col-md-3"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <div className="clearfix"></div>

                  {!model?.id && <div className="col-sm-12"><div className="text-center alert alert-info my-2">Dalšie informácie ako adresa a kontakty sa dajú doplniť po vytvorení delegáta.</div></div>}

                  {model?.id && <>

                    <div className="col-sm-12 col-md-6">
                      <h5>Adresy</h5>
                      {!model?.addresses?.length>0 && <p className='text-muted'>Pridajte adresy ku delegátovi. Prvú adresu
                        môžete <span className="clickable link-primary text-decoration-underline" onClick={()=>addEditAddressModal.current.openModal(null, "App\\Models\\Representative", model.id)}>vytvoriť tu</span>.
                      </p>}
                      {model?.addresses?.length>0 && <div className="row">{model.addresses?.map((address)=>{
                        return (<div className="col-md-12">
                          <AddressCell className="col-sm-12"
                                       key={address.id}
                                       address={address}
                                       edit={()=>addEditAddressModal.current.openModal(address, "App\\Models\\Representative", model.id)}
                                       del={()=>delAddress(address)}
                                       onSuccess={(c)=>onAddEditAddressCallback(c)} />
                        </div>)
                      })}
                        <br />
                        <div className="text-center mt-2">
                          <button type="button" className='btn btn-outline-secondary' onClick={()=>addEditAddressModal.current.openModal(null, "App\\Models\\Representative", model.id)}>
                            <span className="mdi mdi-plus-circle"></span> Pridať</button>
                        </div>
                      </div>}
                    </div>

                    <div className="col-sm-12 col-md-6">
                      <h5>Kontakty</h5>
                      {!model?.contacts?.length>0 && <p className='text-muted'>Pridajte kontakty ku delegátovi. Prvý kontakt
                        môžete <span className="clickable link-primary text-decoration-underline" onClick={()=>addEditContactModal.current.openModal(null, "App\\Models\\Representative", model.id)}>vytvoriť tu</span>.
                      </p>}
                      {model?.contacts?.length>0 && <div className="row">{model.contacts?.map((contact)=>{
                        return (<div className="col-md-12">
                          <ContactCell className="col-sm-12"
                                       key={contact.id}
                                       contact={contact}
                                       edit={()=>addEditContactModal.current.openModal(contact, "App\\Models\\Representative", model.id)}
                                       del={()=>delContact(contact)}
                                       onSuccess={(c)=>onAddEditContactCallback(c)} />
                        </div>)
                      })}
                        <br />
                        <div className="text-center mt-2">
                          <button type="button" className='btn btn-outline-secondary' onClick={()=>addEditContactModal.current.openModal(null, "App\\Models\\Representative", model.id)}><span className="mdi mdi-plus-circle"></span> Pridať</button>
                        </div>
                      </div>}
                    </div>
                  </>}

                  <div className="clearfix"></div>

                </Form>}

              </Section>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
              <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>Odoslať</Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>

    </>
  );
});

export default AddEditRepresentativeModal;
